import React, { useEffect, useState } from 'react';
import {
    IonAlert, IonBackButton, IonButton, IonButtons,
    IonContent, IonHeader, IonIcon, IonPage,
    IonTitle, IonToolbar
} from '@ionic/react';
import { home } from 'ionicons/icons';
import { useHistory } from 'react-router';
import { _defaultButtonColor, _dict, firebaseConfig } from './constants';
import { useGlobalStore } from './stateManagement/globalZustand';
import { useURLStore } from './stateManagement/URLZustand';
import { useStoriesStore } from './stateManagement/storiesZustand';
import { initializeApp } from 'firebase/app';
import { addDoc, collection, getFirestore } from 'firebase/firestore';
import { goToPayment } from './payments';
import { useUserStore } from './stateManagement/userZustand';

const Stories: React.FC = () => {
    const [stories, setStories] = useState<{ [key: string]: any }>({});
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredStories, setFilteredStories] = useState<string[]>([]);
    const [showRequestContent, setShowRequestContent] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const { lang } = useGlobalStore();
    const { getURLTo } = useURLStore();
    const { getListOfStories } = useStoriesStore();
    const { userUniqueId, userEmail, isSubscribed} = useUserStore();
    const history = useHistory();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const stories = await getListOfStories(lang);
                setStories(stories);
                setFilteredStories(Object.keys(stories));
            } catch (error) {
                console.error(error);
            }
        };

        fetchData();
    }, [lang, getListOfStories]);

    const sendSearchQueryToFirestore = async () => {
        try {
            if (searchQuery.length < 5) return;
            const app = initializeApp(firebaseConfig);
            const db = getFirestore(app);
            await addDoc(collection(db, "search"), {
                query: searchQuery,
                timestamp: new Date()
            });
        } catch (error) {
            console.error(error);
        }
    };

    const handleSearchQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    };

    const handleSearchButtonClick = () => {
        sendSearchQueryToFirestore();
        filterStories();
    };

    const filterStories = () => {
        const filtered = Object.keys(stories).filter((key) => {
            return key.toLowerCase().includes(searchQuery.toLowerCase());
        });
        setFilteredStories(filtered);
        setShowRequestContent(filtered.length === 0);
    };

    const handleRequestContent = () => {
        if (isSubscribed) {
            sendRequest();
        }
        setShowAlert(true);
    };

    const sendRequest = async () => {
        if (userUniqueId === null) return;
        if(isSubscribed){
            const app = initializeApp(firebaseConfig);
            const db = getFirestore(app);
            const requestData = { userId: userUniqueId, requestTime: new Date(), query: searchQuery, userEmail: userEmail };
            addDoc(collection(db, "subscribersRequests"), requestData);  
        }
    }
        
    return (
        <IonPage>
            <IonHeader>
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonBackButton defaultHref={getURLTo().home()} />
                        <IonButton onClick={() => { history.push(getURLTo().home()) }}>
                            <IonIcon slot="icon-only" icon={home} />
                        </IonButton>
                    </IonButtons>
                    <IonTitle>{_dict.stories[lang]}</IonTitle>
                </IonToolbar>
            </IonHeader>
            <IonContent>
                <div style={{ overflowX: 'auto' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
                        <input
                            type="text"
                            placeholder={_dict.search[lang]}
                            value={searchQuery}
                            onChange={handleSearchQueryChange}
                            style={{
                                marginTop: "10px",
                                width: '50%',
                                padding: '12px',
                                borderRadius: '4px',
                                border: '1px solid #ccc',
                                fontSize: '20px',
                                height: '60px'
                            }}
                        />
                        <IonButton
                            style={{
                                '--background': _defaultButtonColor,
                                marginLeft: '8px',
                                marginTop: "10px",
                                borderRadius: '4px',
                                fontSize: '20px',
                                height: '60px'
                            }}
                            onClick={handleSearchButtonClick}>{_dict.search[lang]}</IonButton>
                    </div>

                    {filteredStories.length === 0 ? (
                        <>
                            <p>{_dict.noContentFound[lang]}</p>
                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                {showRequestContent && (
                                    <IonButton color="light" onClick={handleRequestContent}>
                                        {_dict.requestContent[lang]}
                                    </IonButton>
                                )}
                            </div>
                            <IonAlert
                                isOpen={showAlert}
                                onDidDismiss={() => setShowAlert(false)}
                                header={_dict.requestContent[lang]}
                                message={isSubscribed ? _dict.yourRequestHasBeenSentSuccessfullyYouWillReceiveAnEmailAsSoonAsTheContentIsAvailable[lang]: _dict.onlySubscribedUsersCanUseThisFeature[lang]}
                                buttons={isSubscribed ? ["OK"]:[
                                    'OK',
                                    {
                                        text: _dict.freeQuotaPremiumBtnText[lang],
                                        handler: () => {
                                            goToPayment(userUniqueId);
                                        }
                                    }
                                ]}
                            />
                        </>
                    ) : (
                        <table style={{ width: '50%', borderCollapse: 'collapse', margin: '0 auto', border: '1px solid black', borderRadius: '4px' }}>
                            <thead>
                                <tr>
                                    <th style={{ border: '1px solid #ccc', padding: '8px', backgroundColor: '#f2f2f2', fontWeight: 'bold', fontSize: '14px' }}>{_dict.name[lang]}</th>
                                    <th style={{ border: '1px solid #ccc', padding: '8px', backgroundColor: '#f2f2f2', fontWeight: 'bold', fontSize: '14px' }}>{_dict.links[lang]}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredStories.map((key) => (
                                    <tr key={key}>
                                        <td style={{ border: '1px solid #ccc', padding: '8px', backgroundColor: '#ffffff', fontSize: '18px' }}>{key}</td>
                                        <td style={{ border: '1px solid #ccc', padding: '8px', backgroundColor: '#ffffff', fontSize: '18px' }}>
                                            <a target="_blank" rel="noopener noreferrer" href={stories[key].link} style={{ color: _defaultButtonColor, textDecoration: 'underline', fontSize: '18px' }}>{_dict.Facebook[lang]}</a>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </IonContent>
        </IonPage>
    );
};

export default Stories;
